import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox, CircularProgress, Box, Fade, Collapse } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ShareOutlined as ShareIcon, DownloadOutlined as DownloadIcon } from "@mui/icons-material"
import { useMission } from "../../../../providers/MissionProvider";
import { makeStyles } from "tss-react/mui";
import { useStation } from "../../../../providers/StationProvider";
import { useCallback, useMemo, useState } from "react";
import { MakeMissionPDF } from "../../../../common/missionpdf";
import { Task } from "@emberly/zenith-client";

const useStyles = makeStyles()(
  (theme) => ({
    content: {
      marginTop: theme.spacing(2)
    },
    taskSelectorContainer: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2)
    }
  })
);



export default function PDFExportDialog(props) {
  const { onClose } = props;

  const { classes } = useStyles();
  const { t } = useTranslation();
  const { station, exportSettings } = useStation();

  const { mission } = useMission();

  const [doc, setDoc] = useState(null);
  const [working, setWorking] = useState(false);


  // Settings
  const [actors, setActors] = useState(true);
  const [contract, setContract] = useState(true);
  const [serviceInfo, setServiceInfo] = useState(false);
  const [images, setImages] = useState(false);
  const [storage, setStorage] = useState(false);
  const [missionNotes, setMissionNotes] = useState(false);
  const [showTasks, setShowTasks] = useState(true);
  const [tasks, setTasks] = useState(mission.salvageTasks.map(t => t.id));

  const hasImages = useMemo(() => Object.values(mission.files.uploads).filter(t => t.type === 1 && t.complete).length > 0, [mission?.files?.uploads]);

  const onExport = async () => {
    try {
      setWorking(true);

      // Delay to smoothly transition to animation
      await Task.Delay(500);

      const settings = { actors, images, contract, serviceInfo, tasks: showTasks ? tasks : [], storage, missionNotes };
      const pdf = await MakeMissionPDF(settings, mission, station, exportSettings, t);
      setDoc(pdf);


    } catch (err) {
      console.log(err);
    } finally {
      setWorking(false);
    }
  };


  const downloadPDF = useCallback(() => {
    doc.save(`mission_${mission.number}_details.pdf`);
    onClose();
  }, [doc, mission, onClose]);

  const sharePDF = useCallback(() => {
    const name = `mission_${mission.number}_details.pdf`;

    try {
      const blob = doc.output("blob", { filename: name });
      const file = new File([blob], name, { type: "application/pdf" });

      navigator.share({
        title: t("mission:pdf:dialog:share"),
        files: [file]
      });


    } catch (err) {
      console.log(err);
      doc.save(name);
    } finally {
      onClose();
    }

  }, [doc, t, mission, onClose]);


  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="alert-dialog-title">
        {t("mission:pdf:dialog:title")}
      </DialogTitle>
      <DialogContent>


        <Collapse in={!!doc && !working}>
          <Grid container direction="row" spacing={1} justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <Button fullWidth variant="contained" size="large" color="primary" onClick={downloadPDF} endIcon={<DownloadIcon />}>
                {t("mission:pdf:dialog:download")}
              </Button>
            </Grid>
            {
              !!navigator.share ? (
                <Grid item xs={12}>
                  <Button fullWidth variant="contained" size="large" color="primary" onClick={sharePDF} endIcon={<ShareIcon />}>
                    {t("mission:pdf:dialog:share")}
                  </Button>
                </Grid>

              ) : null
            }
          </Grid>
        </Collapse>

        <Collapse in={!working && !doc}>

          <Grid container direction="row" spacing={1}>

            <Grid item xs={12}>
              <FormControl>
                <FormLabel>{t("mission:pdf:dialog:settings:description")}</FormLabel>
                <FormGroup>

                  <FormControlLabel
                    label={t("mission:pdf:dialog:settings:actors")}
                    control={
                      <Checkbox
                        checked={actors}
                        onChange={ev => setActors(ev.target.checked)}
                      />
                    }
                  />

                  <FormControlLabel
                    label={t("mission:pdf:dialog:settings:contract")}
                    control={
                      <Checkbox
                        checked={contract}
                        onChange={ev => setContract(ev.target.checked)}
                      />
                    }
                  />

                  <FormControlLabel
                    label={t("mission:pdf:dialog:settings:showTasks")}
                    control={
                      <Checkbox
                        checked={showTasks}
                        onChange={ev => setShowTasks(ev.target.checked)}
                      />
                    }
                  />

                </FormGroup>
              </FormControl>
            </Grid>


            <Grid item xs={12}>
              <Collapse in={showTasks}>
                <Box className={classes.taskSelectorContainer}>
                  <Grid container alignItems="center">

                    <Grid item xs={6} sx={{ justifyContent: "flex-start" }}>
                      <Typography variant="subtitle2">{t("mission:pdf:dialog:settings:tasks")}</Typography>
                    </Grid>

                    <Grid item xs={6} sx={{ justifyContent: "flex-end", display: "flex" }}>
                      <Fade in={tasks.length !== mission.salvageTasks.length}>
                        <Button color="inherit" variant="contained" size="small" onClick={_ => setTasks(mission.salvageTasks.map(t => t.id))}>
                          {t("mission:pdf:dialog:settings:selectAll")}
                        </Button>
                      </Fade>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl>
                        <FormGroup>

                          {
                            mission.salvageTasks.map(
                              (task, idx) =>
                                <FormControlLabel
                                  key={idx}
                                  label={`${t("mission:pdf:task")} ${task.number}${!!task.taskName ? `: ${task.taskName}` : ""}`}
                                  control={
                                    <Checkbox
                                      checked={tasks.includes(task.id)}
                                      onChange={_ => setTasks(tasks.includes(task.id) ? tasks.filter(t => t !== task.id) : [...tasks, task.id])}
                                    />
                                  }
                                />
                            )
                          }

                        </FormGroup>
                      </FormControl>
                    </Grid>

                  </Grid>
                </Box>
              </Collapse>
            </Grid>


            <Grid item xs={12}>
              <FormControl>
                <FormGroup>

                  <FormControlLabel
                    label={t("mission:pdf:dialog:settings:storage")}
                    control={
                      <Checkbox
                        checked={storage && !!mission.storageTask}
                        disabled={!mission.storageTask}
                        onChange={ev => setStorage(ev.target.checked)}
                      />
                    }
                  />

                  <FormControlLabel
                    label={t("mission:pdf:dialog:settings:missionNotes")}
                    control={
                      <Checkbox
                        checked={missionNotes && !!mission.missionNotes}
                        disabled={!mission.missionNotes}
                        onChange={ev => setMissionNotes(ev.target.checked)}
                      />
                    }
                  />

                  <FormControlLabel
                    label={t("mission:pdf:dialog:settings:serviceInfo")}
                    control={
                      <Checkbox
                        checked={serviceInfo && !!mission.serviceInfo}
                        disabled={!mission.serviceInfo}
                        onChange={ev => setServiceInfo(ev.target.checked)}
                      />
                    }
                  />

                  <FormControlLabel
                    label={t("mission:pdf:dialog:settings:images")}
                    control={
                      <Checkbox
                        checked={images && hasImages}
                        disabled={!hasImages}
                        onChange={ev => setImages(ev.target.checked)}
                      />
                    }
                  />

                </FormGroup>
              </FormControl>
            </Grid>


          </Grid>
        </Collapse>

        <Collapse in={working} className={classes.content}>
          <Grid container direction="row" spacing={2} justifyContent="center" alignItems="center">
            <Grid item>
              <CircularProgress size="64px" variant="indeterminate" />
            </Grid>
          </Grid>
        </Collapse>


      </DialogContent>


      <Collapse in={!doc}>
        <DialogActions>
          <Button onClick={onClose}>
            {t("common:close")}
          </Button>
          {
            !doc ? (
              <Button onClick={onExport} variant="contained" color={"primary"} disabled={working}>
                {t("mission:pdf:dialog:run")}
              </Button>
            ) : null
          }
        </DialogActions>
      </Collapse>
    </Dialog >
  );
}
